import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Returns the clamped start timestamp (ms) for event count queries.
 *
 * When a mid-cycle plan conversion occurs, the billing service records the
 * conversion moment as `paidUsageStartAt` on the tenant.  We use that value
 * only when it falls strictly after the current calendar-month start (UTC);
 * otherwise we fall back to the month start so the count covers the full
 * billing month.
 *
 * Note: monthStart is evaluated at call time — stable until the next
 * tenantInfo refresh triggers a re-evaluation.
 */
export function resolveUsageWindowStart(paidUsageStartAt: string | undefined): number {
  const monthStart = dayjs().utc().startOf('month').valueOf();

  if (!paidUsageStartAt) {
    return monthStart;
  }

  const anchorStart = dayjs(paidUsageStartAt).utc().valueOf();

  return anchorStart > monthStart ? anchorStart : monthStart;
}

/**
 * Returns true only when the given ISO string parses to a moment strictly
 * after the current UTC calendar-month start — i.e. a mid-cycle window is
 * active.  Consistent with the clamp logic in resolveUsageWindowStart.
 *
 * Note: monthStart is evaluated at call time — stable until the next
 * tenantInfo refresh triggers a re-evaluation.
 */
export function isUsageWindowMidCycle(isoString: string | undefined): isoString is string {
  if (!isoString) {
    return false;
  }

  const monthStart = dayjs().utc().startOf('month').valueOf();
  const analysisStart = dayjs(isoString).utc().valueOf();

  return analysisStart > monthStart;
}
